import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { validateDateParam } from '../lib/validation';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import Navbar from '../components/Navbar';
import JobCard from '../components/JobCard';
import SortableJobCard from '../components/SortableJobCard';
import { supabase } from '../lib/supabase';
import { getLocalDateString } from '../lib/dates';

function Schedule() {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('daily');
  const rawDate = searchParams.get('date');
  const [selectedDate, setSelectedDate] = useState(
    rawDate && validateDateParam(rawDate) ? rawDate : getLocalDateString()
  );
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [originalJobs, setOriginalJobs] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getDateRange = () => {
    const selected = new Date(selectedDate + 'T00:00:00');
    const start = new Date(selected);
    const end = new Date(selected);

    if (view === 'daily') {
      return { start: selectedDate, end: selectedDate };
    } else if (view === 'weekly') {
      const dayOfWeek = start.getDay();
      start.setDate(start.getDate() - dayOfWeek);
      end.setDate(end.getDate() + (6 - dayOfWeek));
      return {
        start: getLocalDateString(start),
        end: getLocalDateString(end),
      };
    } else {
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
      return {
        start: getLocalDateString(start),
        end: getLocalDateString(end),
      };
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchJobs = async () => {
      setLoading(true);
      const { start, end } = getDateRange();

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id, scheduled_date, urgency_flag, unit_number, job_type_text,
          special_notes, daily_sort_order, property_id,
          properties (id, name, address, city, state, zip, gate_code, access_notes),
          job_tasks (id, task_description, sort_order)
        `)
        .gte('scheduled_date', start)
        .lte('scheduled_date', end)
        .eq('is_deleted', false)
        .order('scheduled_date', { ascending: true })
        .order('daily_sort_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true });

      if (!mounted) return;

      if (!error && data) {
        const sortedJobs = data.map((job) => ({
          ...job,
          job_tasks: (job.job_tasks || []).sort((a, b) => a.sort_order - b.sort_order),
        }));
        setJobs(sortedJobs);
      } else {
        setJobs([]);
      }
      setLoading(false);
    };

    fetchJobs();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, view]);

  const handlePrevPeriod = () => {
    const date = new Date(selectedDate + 'T00:00:00');
    if (view === 'daily') {
      date.setDate(date.getDate() - 1);
    } else if (view === 'weekly') {
      date.setDate(date.getDate() - 7);
    } else {
      date.setMonth(date.getMonth() - 1);
    }
    setSelectedDate(getLocalDateString(date));
  };

  const handleNextPeriod = () => {
    const date = new Date(selectedDate + 'T00:00:00');
    if (view === 'daily') {
      date.setDate(date.getDate() + 1);
    } else if (view === 'weekly') {
      date.setDate(date.getDate() + 7);
    } else {
      date.setMonth(date.getMonth() + 1);
    }
    setSelectedDate(getLocalDateString(date));
  };

  const handleToday = () => {
    setSelectedDate(getLocalDateString());
  };

  const handleStartEditOrder = () => {
    setOriginalJobs([...jobs]);
    setIsEditingOrder(true);
  };

  const handleCancelEditOrder = () => {
    setJobs(originalJobs);
    setIsEditingOrder(false);
  };

  const handleSaveOrder = async () => {
    const reorderData = jobs.map((job, i) => ({ id: job.id, sort_order: i + 1 }));

    // Single-query batch update via RPC; falls back to sequential if not yet installed
    const { error: rpcError } = await supabase.rpc('reorder_jobs', { updates: reorderData });
    if (rpcError) {
      for (const item of reorderData) {
        await supabase.from('jobs').update({ daily_sort_order: item.sort_order }).eq('id', item.id);
      }
    }

    setIsEditingOrder(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = jobs.findIndex((job) => job.id === active.id);
      const newIndex = jobs.findIndex((job) => job.id === over.id);

      const newJobs = arrayMove(jobs, oldIndex, newIndex);
      setJobs(newJobs);
    }
  };

  const todayString = getLocalDateString();

  const getHeaderText = () => {
    const date = new Date(selectedDate + 'T00:00:00');
    if (view === 'daily') {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } else if (view === 'weekly') {
      const { start, end } = getDateRange();
      const startDate = new Date(start + 'T00:00:00');
      const endDate = new Date(end + 'T00:00:00');
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  const groupJobsByDate = () => {
    const grouped = {};
    jobs.forEach((job) => {
      if (!grouped[job.scheduled_date]) {
        grouped[job.scheduled_date] = [];
      }
      grouped[job.scheduled_date].push(job);
    });
    return grouped;
  };

  const groupedJobs = groupJobsByDate();
  const sortedDates = Object.keys(groupedJobs).sort();

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    if (dateStr === todayString) {
      return 'Today';
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const isCurrentPeriod = () => {
    const { start, end } = getDateRange();
    return todayString >= start && todayString <= end;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Schedule</h1>
          <div className="flex items-center gap-2">
            <Link
              to={view === 'daily' ? `/jobs/new?date=${selectedDate}` : '/jobs/new'}
              className="bg-brand-coral text-white px-4 py-2 rounded-md hover:bg-brand-coral-dark text-sm"
            >
              Add Job
            </Link>
            <Link
              to={`/print/${selectedDate}`}
              className="bg-brand-black text-white px-4 py-2 rounded-md hover:bg-brand-gray-dark text-sm"
            >
              Print
            </Link>
          </div>
        </div>

        {/* View Toggle and Navigation */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* View Toggle */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setView('daily')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  view === 'daily'
                    ? 'bg-brand-coral text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Daily
              </button>
              <button
                onClick={() => setView('weekly')}
                className={`px-4 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${
                  view === 'weekly'
                    ? 'bg-brand-coral text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => setView('monthly')}
                className={`px-4 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${
                  view === 'monthly'
                    ? 'bg-brand-coral text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Monthly
              </button>
            </div>

            {/* Date Navigation */}
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrevPeriod}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="text-center min-w-[200px]">
                <p className="text-lg font-semibold text-gray-900">{getHeaderText()}</p>
                {isCurrentPeriod() && view === 'daily' && selectedDate === todayString && (
                  <span className="text-xs text-brand-coral font-medium">Today</span>
                )}
              </div>

              <button
                onClick={handleNextPeriod}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {!isCurrentPeriod() && (
                <button
                  onClick={handleToday}
                  className="text-sm text-brand-coral hover:text-brand-coral-dark ml-2"
                >
                  Today
                </button>
              )}
            </div>

            {/* Job Count */}
            <div className="text-right">
              <p className="text-2xl font-bold text-brand-coral">
                {loading ? '...' : jobs.length}
              </p>
              <p className="text-sm text-gray-500">job{jobs.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading jobs...</div>
        ) : jobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-500 mb-4">No jobs scheduled for this period.</p>
            <Link
              to={view === 'daily' ? `/jobs/new?date=${selectedDate}` : '/jobs/new'}
              className="text-brand-coral hover:text-brand-coral-dark font-medium"
            >
              Add a job
            </Link>
          </div>
        ) : view === 'daily' ? (
          <div>
            {/* Edit Order Controls */}
            <div className="flex justify-end mb-4 gap-2">
              {isEditingOrder ? (
                <>
                  <button
                    onClick={handleCancelEditOrder}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveOrder}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  >
                    Save Order
                  </button>
                </>
              ) : (
                <button
                  onClick={handleStartEditOrder}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 text-sm"
                >
                  Edit Order
                </button>
              )}
            </div>

            {isEditingOrder ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={jobs.map((job) => job.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {jobs.map((job, index) => (
                      <SortableJobCard key={job.id} job={job} orderNumber={index + 1} isEditMode={true} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {jobs.map((job, index) => (
                  <JobCard key={job.id} job={job} orderNumber={index + 1} />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {sortedDates.map((dateStr) => (
              <div key={dateStr}>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  {formatDateHeader(dateStr)}
                  <span className="text-sm font-normal text-gray-500">
                    ({groupedJobs[dateStr].length} job{groupedJobs[dateStr].length !== 1 ? 's' : ''})
                  </span>
                </h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {groupedJobs[dateStr].map((job) => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default Schedule;
